import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';

import { useAppContext } from 'src/components/app-context';

import { ModalBody, ModalFooter } from '@prisjakt/ui/modal';
import { Column } from '@prisjakt/ui/list/variants';
import { BodySmallText, FontEmphasis } from '@prisjakt/ui/text/variants';
import { PrimaryButton } from '@prisjakt/ui/button/variants';

import { EmailHint } from 'src/areas/product-alert/components/product-alert-form/email-hint-styles';
import { Errors } from 'src/areas/product-alert/components/product-alert-form/types';
import { CtxLink } from 'src/components/render-context/ctx-link';
import { TextInput } from 'src/components/inputs';
import Modal from 'src/components/modal-v2';

import { isValidEmail } from 'src/areas/account/helpers';

import type { AppState } from 'src/reducers';
import { StyledLink } from './styles';
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

type Props = {
  closeModal: () => void;
  isOpen: boolean;
  onSubmit: (
    event: React.FormEvent,
    email?: string,
    onError?: (error?: Errors) => void
  ) => void;
};

export const StockStatusAlertModal = (props: Props) => {
  const { closeModal, isOpen, onSubmit } = props;

  const { market, t } = useAppContext();

  const isAuthenticated = useIsAuthenticated();

  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<Errors | undefined>(undefined);

  const errorState = useSelector(
    (state: AppState) => state.areas.productAlert.error
  );
  const successState = useSelector(
    (state: AppState) => state.areas.productAlert.success
  );

  useEffect(() => {
    if (errorState) {
      switch (errorState) {
        case Errors.ERROR_EMAIL_INVALID:
        case Errors.ERROR_EMAIL_IN_USE: {
          setError(errorState);
          break;
        }
        default: {
          setError(Errors.ERROR_UNKNOWN);
        }
      }
    } else if (successState) {
      setEmail('');
      setError(undefined);
      closeModal();
    }
  }, [errorState, successState]);

  const privacyPolicyUri = market?.privacySettings?.responsive?.privacyUrl;

  const handleEmailChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setError(
        isValidEmail(event.target.value)
          ? undefined
          : Errors.ERROR_EMAIL_INVALID
      );
      setEmail(event.target.value);
    },
    500
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={t('product', 'alert.stock-status-alert-email')}
    >
      <form
        method="post"
        onSubmit={(event: React.FormEvent) => onSubmit(event, email, setError)}
        noValidate
      >
        {!isAuthenticated && (
          <ModalBody>
            <Column gap={1.5}>
              <TextInput
                autoFocus
                type="email"
                name="email"
                defaultValue={email}
                onChange={handleEmailChange}
                placeholder={t('alerts', 'alert-form.email-placeholder')}
                tabIndex={0}
                // Do not send this element to CUX (cux.io)
                data-cux-exclude
                variant="rounded"
              />
              <EmailHint visible={error === Errors.ERROR_EMAIL_IN_USE}>
                {t('alerts', 'alert-form.email-in-use-error')}
                &nbsp;
                <CtxLink to="/login">
                  {t('alerts', 'alert-form.login-error')}
                </CtxLink>
              </EmailHint>
              <EmailHint error visible={error === Errors.ERROR_EMAIL_INVALID}>
                {t('alerts', 'alert-form.invalid-email-error')}
              </EmailHint>
              {privacyPolicyUri && (
                <StyledLink>
                  <CtxLink to={privacyPolicyUri}>
                    <BodySmallText emphasis={FontEmphasis.MEDIUM}>
                      {t('product', 'alert.how-your-email-is-used')}
                    </BodySmallText>
                  </CtxLink>
                </StyledLink>
              )}
            </Column>
          </ModalBody>
        )}
        <ModalFooter>
          <PrimaryButton disabled={Boolean(error)} htmlType="submit" fullWidth>
            {t('product', 'alert.create-stock-status-alert')}
          </PrimaryButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};
